// src/admin/components/reports/widgets/RecentPaymentsWidget.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  RiMoneyDollarCircleLine,
  RiAlertLine,
  RiCalendarLine,
  RiUserLine,
  RiPrinterLine
} from 'react-icons/ri';
import { getPaymentLogs, formatCurrency, formatDate } from '../../../../models/reportModel';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';

const RecentPaymentsWidget = ({ isDarkMode }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await getPaymentLogs({ limit: 5 });
        setPayments(response.paymentLogs || []);
      } catch (err) {
        console.error(t('error_fetching_payments'), err);
        setError(err.message || t('failed_load_payments'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [t]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-t-2 border-red-500"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
      } border flex items-center h-64 justify-center`}>
        <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
        {error}
      </div>
    );
  }
  
  if (payments.length === 0) {
    return (
      <div className={`text-center p-8 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
        {t('no_recent_payments')}
      </div>
    );
  }
  
  const getPaymentTypeLabel = (type) => {
    switch(type) {
      case 'rent': return t('rent');
      case 'service': return t('service');
      case 'deposit': return t('deposit');
      default: return type;
    }
  };

  return (
    <div className="space-y-4" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      {payments.map(payment => (
        <div 
          key={payment.paymentId}
          className={`p-3 rounded-lg ${
            isDarkMode ? 'bg-zinc-800 hover:bg-gray-650' : 'bg-gray-50 hover:bg-gray-100'
          } transition-colors duration-200`}
        >
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
              <div className={`h-10 w-10 rounded-full ${
                payment.paymentType === 'rent' 
                  ? 'bg-zinc-500' 
                  : payment.paymentType === 'service'
                    ? 'bg-green-500'
                    : 'bg-purple-500'
              } flex items-center justify-center ${isRTL ? 'ml-3' : 'mr-3'}`}>
                <RiMoneyDollarCircleLine className="text-white" />
              </div>
              
              <div className={isRTL ? 'text-right' : 'text-left'}>
                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(payment.amount)}
                </p>
                <div className={`flex items-center text-sm ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <RiUserLine className={`${isRTL ? 'ml-1' : 'mr-1'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={14} />
                  <span className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {payment.tenantName}
                  </span>
                </div>
              </div>
            </div>
            
            <div className={isRTL ? 'text-left' : 'text-right'}>
              <div className={`text-xs py-1 px-2 rounded-full inline-block ${
                payment.paymentType === 'rent' 
                  ? isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-700'
                  : payment.paymentType === 'service'
                    ? isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-700'
                    : isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-700'
              }`}>
                {getPaymentTypeLabel(payment.paymentType)}
              </div>
              <div className={`flex items-center text-xs mt-1 ${isRTL ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
                <RiCalendarLine className={`${isRTL ? 'ml-1' : 'mr-1'} ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`} size={12} />
                <span className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {formatDate(payment.paymentDate)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default RecentPaymentsWidget;